import {useCallback, useEffect, useReducer} from 'react';
import {
    clearEOYRemindersStoreData,
    clearPrevCompletedStepUser,
    clearStepAssignBy,
    eoyCompletedStepUserKey,
    eoyOwnerKey,
    eoyResponsibleKey,
    eoyStepAssignByKey,
    getEoyFiscalYear,
    getEoyStoredData,
    showEOYReminder
} from '../../actions/reminders';
import {getUsername} from '../../../../_shared/GWTBridge/invoke/genPage';
import {Form} from '../../../../stores/Form/Form';
import {
    checkEoyRecord,
    getEoyLog,
    getLastUnfinishedStep,
    isWizardFinished
} from '../../actions/wizardLog';
import {
    displayReminder,
    displayReminderForUpcomingYear,
    getReminderType,
    getStages,
    getStepName
} from './utils';
import {ReminderTypes} from './enum';
import {getUserFullName} from '../../actions/user';

function reducer(state, payload) {
    return {...state, ...(payload || {})};
}

export function useEOYReminders(currentCompany: string) {
    const [state, dispatch] = useReducer(reducer, {
        stages: [],
        activeStageIndex: null,
        reminderType: null,
        stepTitle: '',
        lastUnfinishedStep: '',
        prevStepTitle: '',
        prevStepFullUserName: '',
        stepAssignByFullName: ''
    });

    const {reminderType} = state;

    const onClose = useCallback(async () => {
        if (
            reminderType === ReminderTypes.stepCompleted ||
            reminderType === ReminderTypes.stepAssigned
        ) {
            await clearPrevCompletedStepUser();
            await clearStepAssignBy();
        }
        showEOYReminder(false);
    }, [reminderType]);

    useEffect(() => {
        (async () => {
            const res = await getEoyStoredData();
            const ownerUserName = res[eoyOwnerKey]?.Str;
            const isOwner = ownerUserName === getUsername();

            let responsibleUser = res[eoyResponsibleKey]?.Str;
            let isUserResponsible = responsibleUser === getUsername();

            if (isUserResponsible || isOwner) {
                const form: Form = await getEoyLog();

                if (form) {
                    const isOk: boolean = checkEoyRecord(form);
                    const isFinished: boolean = isWizardFinished(form);

                    if (responsibleUser !== ownerUserName && isFinished) {
                        await clearEOYRemindersStoreData(ownerUserName);
                        responsibleUser = ownerUserName;
                        isUserResponsible = isOwner;
                    }

                    if (isUserResponsible && isOk) {
                        const lastUnfinishedStep = isFinished
                            ? '20'
                            : (await getLastUnfinishedStep(form)) || '';

                        if (lastUnfinishedStep) {
                            const fiscalYear = await getEoyFiscalYear(form);
                            const stages = getStages(lastUnfinishedStep);
                            const activeStageIndex = stages.findIndex(stage => stage.isActive);

                            const prevStepData = res[eoyCompletedStepUserKey]?.Str.split(':') || [];
                            const [prevStepId, prevStepUser] = prevStepData || [];
                            const stepAssignBy = res[eoyStepAssignByKey]?.Str || '';

                            const reminderType = getReminderType({
                                currentWizardStep: lastUnfinishedStep,
                                activeStageIndex,
                                prevStepUser,
                                stepAssignBy,
                                isOwner,
                                newStartDate: fiscalYear.newStartDate,
                                currentEndDate: fiscalYear.currentEndDate
                            });

                            const show = isFinished
                                ? displayReminderForUpcomingYear(fiscalYear.newEndDate)
                                : displayReminder(
                                      activeStageIndex,
                                      lastUnfinishedStep,
                                      fiscalYear.newStartDate,
                                      fiscalYear.currentEndDate,
                                      reminderType
                                  );

                            const prevStepFullUserName =
                                reminderType === ReminderTypes.stepCompleted ||
                                reminderType === ReminderTypes.stageCompleted
                                    ? await getUserFullName(prevStepUser)
                                    : '';
                            const stepAssignByFullName =
                                reminderType === ReminderTypes.stepAssigned
                                    ? await getUserFullName(stepAssignBy)
                                    : '';

                            dispatch({
                                stages,
                                lastUnfinishedStep,
                                activeStageIndex,
                                stepTitle: getStepName(lastUnfinishedStep),
                                prevStep: getStepName(prevStepId),
                                reminderType,
                                prevStepFullUserName,
                                stepAssignByFullName
                            });
                            showEOYReminder(show);
                        }
                    }
                }
            } else {
                showEOYReminder(false);
            }
        })();
    }, [currentCompany]);

    return {
        ...state,
        onClose
    };
}
